import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Tabla densa. Es la pantalla donde un contador pasa horas.
 *
 * Decisiones que no son cosmeticas:
 * - Filas de 28px y filetes de 1px: cabe un mes de movimientos sin scroll lateral.
 * - Cabecera pegajosa opaca: al bajar 400 filas hay que seguir sabiendo que columna es cual.
 * - Cabecera en versalitas y color apagado: el titulo de columna no compite con el dato.
 * - `border-separate` con `border-spacing: 0` para que la cabecera pegajosa
 *   conserve su filete inferior (con `collapse` el navegador lo pierde al hacer sticky).
 */
const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & { containerClassName?: string }
>(({ className, containerClassName, ...props }, ref) => (
  <div className={cn("relative w-full overflow-auto", containerClassName)}>
    <table
      ref={ref}
      className={cn(
        "w-full caption-bottom border-separate border-spacing-0 text-xs",
        "[&_tbody_tr:last-child>td]:border-b-0",
        className,
      )}
      {...props}
    />
  </div>
))
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      "sticky top-0 z-20 [&_th]:bg-surface-sunken",
      className,
    )}
    {...props}
  />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <tbody ref={ref} className={cn(className)} {...props} />)
TableBody.displayName = "TableBody"

/** Los totales quedan pegados abajo: sumar es el motivo de mirar la tabla. */
const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "sticky bottom-0 z-20 font-semibold",
      "[&_td]:border-b-0 [&_td]:border-t-[3px] [&_td]:border-double [&_td]:border-border-strong",
      "[&_td]:bg-surface-sunken [&_td]:py-1.5",
      className,
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "group/row bg-surface transition-colors duration-75",
        "hover:bg-row-hover data-[state=selected]:bg-row-selected",
        "focus-visible:outline-none",
        className,
      )}
      {...props}
    />
  ),
)
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-row-head overflow-hidden text-ellipsis whitespace-nowrap border-b border-border-strong/60 px-2 text-left align-middle",
      "text-2xs font-semibold uppercase tracking-[0.07em] text-muted-foreground",
      className,
    )}
    {...props}
  />
))
TableHead.displayName = "TableHead"

/**
 * Columna fijada al borde derecho para las acciones de fila. En una tabla de
 * 15 columnas, tener que scrollear horizontalmente para llegar a "Aprobar" es
 * el defecto que hace que la gente deje de usar el teclado y el mouse por igual.
 * `bg-inherit` toma el fondo de la fila (hover / seleccionada / cursor).
 */
export const stickyActionCell =
  "sticky right-0 z-[2] bg-inherit shadow-[-1px_0_0_0_hsl(var(--border))]"
export const stickyActionHead =
  "sticky right-0 z-30 shadow-[-1px_0_0_0_hsl(var(--border))]"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    /*
      `overflow-hidden` NO es cosmetica: es lo que hace que un ancho de columna
      SEA un ancho.

      En una tabla `table-fixed`, una celda con `whitespace-nowrap` cuyo texto
      no cabe no se recorta: se DESBORDA sobre la celda vecina y las dos se leen
      como una sola. En la cola de revision eso pintaba «17/07/202601» —la fecha
      pegada al tipo de comprobante— y «FN01-4869898820100047218» —la serie
      pegada al RUC—. Nadie lo lee como dos columnas: lo lee como una fecha
      malformada y un identificador que no existe.

      Con `overflow-hidden`, lo que no cabe se corta; la celda que ademas lleve
      `truncate` mostrara los puntos suspensivos y su `title`. Cortar es
      recuperable —se ve que falta algo—; invadir la columna de al lado no, y
      ademas falsifica el dato del vecino.
    */
    className={cn(
      "h-row overflow-hidden border-b border-border/70 px-2 py-1 align-middle",
      className,
    )}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-2 px-2 text-left text-2xs text-muted-foreground", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
